import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { DocumentViewer } from "@/components/ui/document-workspace";
import { Feedback, useResource } from "@/components/workflow/shared";
import { equipmentLabelsApi } from "@/lib/api/equipmentLabels";
import { errorMessage } from "@/lib/api";
import { datum } from "@/lib/format";
import { Field, useEquipmentAccess } from "./shared";

export function EquipmentLabels() {
  const access = useEquipmentAccess();
  const [article, setArticle] = useState("");
  const [place, setPlace] = useState("");
  const [unprinted, setUnprinted] = useState(false);
  const [offset, setOffset] = useState(0);
  const pageSize = 100;
  const resource = useResource(
    `equipment-labels:${article}:${place}:${unprinted}:${offset}`,
    useCallback(
      async (signal) => {
        if (!access.catalogue) return { page: null, history: [] };
        const [page, history] = await Promise.all([
          equipmentLabelsApi.list(
            {
              artikl: article || undefined,
              mjesto: place || undefined,
              bez_naljepnice: unprinted,
              limit: pageSize,
              offset,
            },
            signal,
          ),
          equipmentLabelsApi.history(signal),
        ]);
        return { page, history };
      },
      [access.catalogue, article, place, unprinted, offset],
    ),
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [document, setDocument] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const objectUrl = useRef("");
  const request = useRef<AbortController | null>(null);
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    };
  }, []);
  if (!access.catalogue)
    return (
      <p role="alert">Za naljepnice je potrebna ovlast nad svom opremom.</p>
    );
  const visible = resource.data?.page?.komadi ?? [];
  const changeFilter = (update: () => void) => {
    update();
    setSelected([]);
    setOffset(0);
  };
  async function open(id: string) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError("");
    try {
      const blob = await equipmentLabelsApi.document(id, controller.signal);
      if (!controller.signal.aborted && mounted.current) {
        if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = URL.createObjectURL(blob);
        setUrl(objectUrl.current);
      }
    } catch (cause) {
      if (!controller.signal.aborted && mounted.current)
        setError(errorMessage(cause, "PDF naljepnica nije dostupan."));
    } finally {
      if (!controller.signal.aborted && mounted.current) setBusy(false);
    }
  }
  async function create() {
    if (saving.current || !selected.length) return;
    saving.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const sheet = await equipmentLabelsApi.create(selected);
      if (!mounted.current) return;
      setDocument(sheet.id);
      setSelected([]);
      setMessage(
        `PDF je spremljen. Naljepnice označene kao ispisane: ${datum(sheet.ispisano_at)}.`,
      );
      resource.reload();
    } catch (cause) {
      if (mounted.current)
        setError(errorMessage(cause, "Naljepnice nisu spremljene."));
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <section className="equipment-form" aria-label="Naljepnice opreme">
      <h2 className="text-xl font-bold">Naljepnice</h2>
      <p>
        A4 · 3 × 8 naljepnica · 70 × 37 mm. Ispišite u stvarnoj veličini (100
        %), bez prilagodbe papiru.
      </p>
      <p>
        Izrada PDF-a označava odabrane naljepnice ispisanima. Ponovni ispis je
        dopušten. Skele i oplate nemaju naljepnice.
      </p>
      <Feedback error={resource.error} />
      {resource.error && (
        <Button onClick={resource.reload}>Pokušaj ponovno</Button>
      )}
      {resource.loading && <p role="status">Učitavanje naljepnica…</p>}
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">1</span> Odaberi komade
        </legend>
        <div className="equipment-actions">
          <Field label="Artikl">
            <select
              value={article}
              disabled={busy || resource.loading}
              onChange={(e) => changeFilter(() => setArticle(e.target.value))}
            >
              <option value="">Svi artikli</option>
              {(resource.data?.page?.artikli ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.naziv}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Mjesto">
            <select
              value={place}
              disabled={busy || resource.loading}
              onChange={(e) => changeFilter(() => setPlace(e.target.value))}
            >
              <option value="">Sva mjesta</option>
              {(resource.data?.page?.mjesta ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.naziv}
                </option>
              ))}
            </select>
          </Field>
          <label className="flex min-h-12 items-center gap-2">
            <input
              type="checkbox"
              checked={unprinted}
              disabled={busy || resource.loading}
              onChange={(e) =>
                changeFilter(() => setUnprinted(e.target.checked))
              }
            />
            Bez naljepnice
          </label>
        </div>
        <Button
          variant="outline"
          disabled={busy || !visible.length}
          onClick={() =>
            setSelected(
              selected.length ? [] : visible.slice(0, 240).map((row) => row.id),
            )
          }
        >
          {selected.length ? "Poništi odabir" : "Odaberi prikazane (do 240)"}
        </Button>
        {visible.map((row) => (
          <label key={row.id} className="equipment-label-row">
            <input
              type="checkbox"
              aria-label={`Odaberi ${row.inventarni_broj}`}
              disabled={
                busy || (!selected.includes(row.id) && selected.length >= 240)
              }
              checked={selected.includes(row.id)}
              onChange={(e) =>
                setSelected((ids) =>
                  e.target.checked
                    ? [...ids, row.id]
                    : ids.filter((id) => id !== row.id),
                )
              }
            />
            <span>
              <b>
                {row.inventarni_broj} · {row.naziv}
              </b>
              <small>
                {row.mjesto} ·{" "}
                {row.naljepnica_ispisana_at
                  ? `Naljepnica ispisana ${datum(row.naljepnica_ispisana_at)}`
                  : "Bez naljepnice"}
              </small>
            </span>
          </label>
        ))}
        {!resource.loading && !resource.error && !visible.length && (
          <p>Nema komada za odabrane filtre.</p>
        )}
        {resource.data?.page && (
          <nav className="equipment-actions" aria-label="Stranice naljepnica">
            <Button
              variant="outline"
              disabled={busy || resource.loading || offset === 0}
              onClick={() => {
                setSelected([]);
                setOffset((current) => Math.max(0, current - pageSize));
              }}
            >
              Prethodna stranica
            </Button>
            <span>
              {visible.length ? offset + 1 : 0}–{offset + visible.length} od{" "}
              {resource.data.page.ukupno} komada
            </span>
            <Button
              variant="outline"
              disabled={
                busy ||
                resource.loading ||
                offset + pageSize >= resource.data.page.ukupno
              }
              onClick={() => {
                setSelected([]);
                setOffset((current) => current + pageSize);
              }}
            >
              Sljedeća stranica
            </Button>
          </nav>
        )}
      </fieldset>
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">2</span> Izradi naljepnice
        </legend>
        <Button
          disabled={busy || !selected.length}
          onClick={() => void create()}
        >
          Izradi PDF i označi ispisano ({selected.length})
        </Button>
        {error && (
          <p role="alert" className="equipment-error">
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="equipment-success">
            {message}
          </p>
        )}
        {document && (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => void open(document)}
          >
            Otvori / Ispiši PDF naljepnica
          </Button>
        )}
      </fieldset>
      {!!resource.data?.history.length && (
        <section className="equipment-card">
          <Button
            type="button"
            variant="outline"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((old) => !old)}
          >
            Prethodni ispisi (posljednjih 20)
          </Button>
          {historyOpen && (
            <div className="equipment-field">
              {resource.data.history.map((sheet) => (
                <div className="equipment-row" key={sheet.id}>
                  <span>
                    {datum(sheet.ispisano_at)} · {sheet.broj_komada} komada
                  </span>
                  <Button
                    disabled={busy}
                    variant="outline"
                    onClick={() => void open(sheet.id)}
                  >
                    Otvori PDF naljepnica
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      {url && (
        <Dialog
          fullscreen
          title="Naljepnice opreme"
          onClose={() => {
            URL.revokeObjectURL(objectUrl.current);
            objectUrl.current = "";
            setUrl("");
          }}
        >
          <div className="equipment-actions">
            <Button
              variant="outline"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              Otvori za ispis
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const download = window.document.createElement("a");
                download.href = url;
                download.download = "Naljepnice.pdf";
                download.click();
              }}
            >
              Preuzmi PDF
            </Button>
          </div>
          <DocumentViewer
            url={url}
            mime="application/pdf"
            title="Naljepnice opreme"
          />
        </Dialog>
      )}
    </section>
  );
}
